import React, { useState } from 'react'
import type { TCustomEndpointInput } from '@homepal/api-contract'

import Alert from 'components_new/atoms/Alert'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { TCustomEndpointLabels } from 'utils/sources'
import { suggestEndpoints } from 'utils/customEndpoints'

interface AddEndpointsBulkDialogProps {
  labels: TCustomEndpointLabels
  onClose: () => void
  onSubmit: (
    endpoints: TCustomEndpointInput[],
    callback: (err?: boolean) => void
  ) => void
  open: boolean
}

type TStep = 'paste' | 'preview'

/**
 * Two steps: paste a list (with an optional marker to strip from the table
 * names), then review the rows and edit any name before they are sent. The
 * backend gets exactly what the preview shows, nothing is parsed there.
 */
const AddEndpointsBulkDialog = (props: AddEndpointsBulkDialogProps) => {
  const { labels, onClose, onSubmit, open } = props

  const [step, setStep] = useState<TStep>('paste')
  const [text, setText] = useState('')
  const [affix, setAffix] = useState('')
  const [rows, setRows] = useState<TCustomEndpointInput[]>([])
  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)

  const singularLower = labels.singular.toLowerCase()
  const pluralCapitalized =
    labels.plural[0].toUpperCase() + labels.plural.slice(1)
  const count = (n: number) => `${n} ${n === 1 ? singularLower : labels.plural}`

  const pastedCount = suggestEndpoints(text, affix).length

  const handleClose = () => {
    setStep('paste')
    setText('')
    setAffix('')
    setRows([])
    setFailed(false)
    setSaving(false)
    onClose()
  }

  const handleNext = () => {
    setRows(suggestEndpoints(text, affix))
    setFailed(false)
    setStep('preview')
  }

  const handleChangeRow = (
    index: number,
    field: keyof TCustomEndpointInput,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    )
  }

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_row, i) => i !== index))
  }

  const cleanRows: TCustomEndpointInput[] = rows.map((row) => ({
    technical_name: row.technical_name.trim(),
    table_name: labels.skipCorresponding
      ? row.technical_name.trim()
      : row.table_name.trim()
  }))

  const hasEmpty = cleanRows.some(
    (row) => !row.technical_name || !row.table_name
  )
  const hasDuplicate =
    new Set(cleanRows.map((row) => row.technical_name)).size !==
    cleanRows.length

  const handleSubmit = () => {
    setFailed(false)
    setSaving(true)

    onSubmit(cleanRows, (err) => {
      setSaving(false)

      if (err) {
        setFailed(true)
      } else {
        handleClose()
      }
    })
  }

  return (
    <SimpleDialog
      actions={
        step === 'paste' ? (
          <>
            <Button onClick={handleClose} variant="text">
              Avbryt
            </Button>
            <Button
              disabled={pastedCount === 0}
              onClick={handleNext}
              variant="contained"
            >
              Nästa
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('paste')} variant="text">
              Tillbaka
            </Button>
            <Button
              disabled={rows.length === 0 || hasEmpty || hasDuplicate}
              loading={saving}
              onClick={handleSubmit}
              variant="contained"
            >
              {`Lägg till ${count(rows.length)}`}
            </Button>
          </>
        )
      }
      fullWidth={true}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      title={`Lägg till flera ${labels.plural}`}
    >
      {step === 'paste' ? (
        <>
          <TextField
            fullWidth={true}
            helperText="En per rad eller separerade med komma."
            label={pluralCapitalized}
            minRows={6}
            multiline={true}
            onChange={(event) => setText(event.target.value)}
            value={text}
          />

          {labels.skipCorresponding ? null : (
            <TextField
              fullWidth={true}
              helperText={`Tas bort i början eller slutet av namnet när ${labels.corresponding.toLowerCase()} föreslås, t.ex. HOMEPAL_. Du kan ändra varje namn i nästa steg.`}
              label={`Ta bort ur ${labels.corresponding.toLowerCase()}`}
              onChange={(event) => setAffix(event.target.value)}
              value={affix}
            />
          )}

          {pastedCount > 0 ? (
            <Text color="text.secondary" variant="body2">
              {`${count(pastedCount)} hittades.`}
            </Text>
          ) : null}
        </>
      ) : (
        <>
          <Text color="text.secondary" variant="body2">
            {`Granska ${labels.plural} innan de läggs till. Namnen går att ändra.`}
          </Text>

          <Stack spacing={1}>
            {rows.map((row, index) => (
              <Stack
                alignItems="center"
                direction="row"
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                spacing={1}
              >
                <TextField
                  error={!row.technical_name.trim()}
                  fullWidth={true}
                  label={labels.singular}
                  onChange={(event) =>
                    handleChangeRow(index, 'technical_name', event.target.value)
                  }
                  size="small"
                  value={row.technical_name}
                />
                {labels.skipCorresponding ? null : (
                  <TextField
                    error={!row.table_name.trim()}
                    fullWidth={true}
                    label={labels.corresponding}
                    onChange={(event) =>
                      handleChangeRow(index, 'table_name', event.target.value)
                    }
                    size="small"
                    value={row.table_name}
                  />
                )}
                <TooltipWrapper title="Ta bort">
                  <IconButton
                    onClick={() => handleRemoveRow(index)}
                    size="small"
                  >
                    <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
                  </IconButton>
                </TooltipWrapper>
              </Stack>
            ))}
          </Stack>

          {hasDuplicate ? (
            <Alert severity="warning">
              {`Samma ${singularLower} förekommer mer än en gång.`}
            </Alert>
          ) : null}

          {failed ? (
            <Alert severity="error">
              {`Kunde inte läggas till. Ingen ${singularLower} lades till, kontrollera att inget namn redan används.`}
            </Alert>
          ) : null}
        </>
      )}
    </SimpleDialog>
  )
}

export default AddEndpointsBulkDialog
